import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api.service';
import BlogTable from '../../../components/BlogTable';
import './BlogList.css';

const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const blogsPerPage = 10;

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, searchTerm, statusFilter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminBlogs();
      if (response.data.success) {
        setBlogs(response.data.data);
      } else {
        setError('Failed to load blogs');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load blogs');
      console.error('Blogs fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(blog => 
        blog.blog_title?.toLowerCase().includes(searchLower) ||
        blog.blog_by?.toLowerCase().includes(searchLower) ||
        blog.blog?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(blog => blog.display_status === statusFilter);
    }

    setFilteredBlogs(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleDelete = async (blogId) => {
    try {
      setError(null);
      const response = await apiService.deleteBlog(blogId);
      if (response.data.success) {
        setSuccessMessage('Blog deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchBlogs(); // Refresh the list
      } else {
        setError('Failed to delete blog');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete blog');
      console.error('Blog delete error:', err);
    }
  };

  const handleEdit = (blogId) => {
    navigate(`/admin/blogs/edit/${blogId}`);
  };

  const handleAddNew = () => {
    navigate('/admin/blogs/new');
  };

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => paginate(1)} className="page-btn">
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="page-dots">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`page-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" className="page-dots">...</span>);
      }
      pages.push(
        <button key="last" onClick={() => paginate(totalPages)} className="page-btn">
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="blog-list-container">
      <div className="blog-list-header">
        <h2>Blog Management</h2>
        <button className="btn btn-primary" onClick={handleAddNew}>
          + Add New Blog
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <div className="blog-list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search blogs by title, author, or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <div className="blog-list-stats">
        <span>
          Showing {currentBlogs.length > 0 ? indexOfFirstBlog + 1 : 0} - {Math.min(indexOfLastBlog, filteredBlogs.length)} of {filteredBlogs.length} blogs
          {searchTerm || statusFilter !== 'all' ? ` (filtered from ${blogs.length} total)` : ''}
        </span>
      </div>

      <BlogTable
        blogs={currentBlogs}
        onDelete={handleDelete}
        onEdit={handleEdit}
        loading={loading}
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {renderPagination()}
          <button
            className="page-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogList;

